import { isCargoEventTimeData, type TimeData } from '../core/TimeData';
import {
  createPnlProcessorContext,
  type PnlProcessorContextInput,
} from './context/context';
import {
  BunkerCostProcessor,
  type BunkerCostProcessorInput,
} from './bunker/bunkerProcessor';
import { CostSharingProcessor } from './costSharing/costSharing';
import { type BunkerCost } from './bunker/bunkerCost';
import { Co2CostProcessor, type Co2ProcessorInput } from './co2/co2Processor';
import { type Co2Cost } from './co2/co2Cost';
import type { PnlProcessorError } from './processor';
import type {
  FreightCost,
  FreightIncome,
  VoyageCharterLumpsumCost,
  VoyageCharterLumpsumIncome,
} from './freight/freightPnl';
import {
  FreightProcessor,
  type FreightProcessorInput,
} from './freight/freightProcessor';
import type { ScenarioOrder } from '../../lib/planningData';

export type PnlCost =
  | BunkerCost
  | Co2Cost
  | FreightCost
  | VoyageCharterLumpsumCost;
export type PnlIncome = FreightIncome | VoyageCharterLumpsumIncome;
export type PnlItem = PnlCost | PnlIncome;

const getTimeDataOrders = (
  timeData: TimeData[],
  orderById: Map<number, ScenarioOrder>,
) => {
  const orders = new Map<number, ScenarioOrder>();

  for (const td of timeData) {
    if (isCargoEventTimeData(td)) {
      const order = orderById.get(td.event.order.id);
      if (order) {
        orders.set(order.id, order);
      }
    }
  }

  return orders;
};

export interface CalculatePnlInputs {
  bunkerCost: BunkerCostProcessorInput;
  co2Cost: Co2ProcessorInput;
  context: PnlProcessorContextInput;
  freight: FreightProcessorInput;
}

export type CalculatePnlResult = ReturnType<typeof calculatePnl>;

export function calculatePnl(timeData: TimeData[], inputs: CalculatePnlInputs) {
  const context = createPnlProcessorContext(inputs.context);

  const timeDataOrders = getTimeDataOrders(timeData, inputs.freight.orderById);

  // Cost sharing details need to be calculated in a separate loop due to
  // limitations in determining shipment boundaries, this way other processors
  // do not need to implement buffering of events
  const costSharingProcessor = new CostSharingProcessor(context);
  for (const td of timeData) {
    costSharingProcessor.processEvent(td);
  }

  // TODO: Maybe implement a check to ensure that finalize has been called
  // before running other processors
  costSharingProcessor.finalize();

  const bunkerProcessor = new BunkerCostProcessor(inputs.bunkerCost, context);
  const co2Processor = new Co2CostProcessor(inputs.co2Cost, context);
  const freightProcessor = new FreightProcessor(
    {
      ...inputs.freight,
      orderById: timeDataOrders,
    },
    context,
  );

  const processors = [bunkerProcessor, co2Processor, freightProcessor] as const;

  for (const td of timeData) {
    for (const processor of processors) {
      processor.processEvent(td);
    }
  }

  for (const processor of processors) {
    processor.finalize();
  }

  const costs: PnlCost[] = [];
  const income: PnlIncome[] = [];
  const errors: PnlProcessorError[] = [];

  for (const processor of processors) {
    const result = processor.getResult();

    if (result.costs) costs.push(...result.costs);
    if (result.errors) errors.push(...result.errors);
    if (result.income) income.push(...result.income);
  }

  const extras = {
    co2Processor: {
      co2Emissions: co2Processor.getResult().co2Emissions,
    },
  };

  return {
    costs,
    errors,
    extras,
    income,
  };
}
